<template>
  <div class="container flex-grow-1 d-flex flex-column my-4">
    <Loading v-if="loading" />
    <div v-if="microsoftLinked === false" class="card border-light-subtle shadow-sm mb-4">
      <div class="card-body d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
        <div class="d-flex align-items-center gap-3">
          <img src="../assets/microsoft-logo.svg" alt="" width="32" height="32" />
          <div>
            <p class="card-text text-secondary mb-0">
              {{ $t("home.microsoft_account_description") }}
            </p>
          </div>
        </div>
        <button type="button"
          class="btn btn-primary text-white text-nowrap align-self-start align-self-md-center sign-in-with-microsoft"
          :disabled="linkingMicrosoft" @click="linkMicrosoftAccount">
          <span v-if="linkingMicrosoft" class="spinner-border spinner-border-sm me-2" role="status"
            aria-hidden="true"></span>
          {{ $t("home.link_microsoft_account") }}
        </button>
      </div>
    </div>

    <div v-if="!loading && hasNoTools" class="d-flex flex-column align-items-center justify-content-center flex-grow-1">
      <div class="text-center">
        <font-awesome-icon :icon="['far', 'browser']" size="3x" class="mb-3 text-secondary" />
        <p class="text-secondary">
          {{ $t("home.empty_message") }}
        </p>
      </div>
    </div>

    <div v-else-if="!loading" class="row">
      <div class="col-12">
        <div
          class="d-flex flex-column flex-lg-row gap-3 align-items-stretch align-items-lg-center justify-content-between mb-4">
          <div class="input-group">
            <span class="input-group-text">
              <font-awesome-icon :icon="['far', 'magnifying-glass']" />
            </span>
            <input type="text" class="form-control" :placeholder="$t('home.search_placeholder')" v-model="searchTerm" />
            <button v-if="searchTerm" class="btn btn-secondary" type="button" :aria-label="$t('home.clear_search')"
              @click="searchTerm = ''">
              <font-awesome-icon :icon="['far', 'xmark']" class="text-white" />
            </button>
          </div>

          <div class="d-flex align-items-center justify-content-end gap-3">
            <!-- <div class="form-check form-switch mb-0">
              <input
                class="form-check-input"
                type="checkbox"
                role="switch"
                id="switch-app-item-icons"
                v-model="showIcons"
              />
              <label
                class="form-check-label"
                for="switch-app-item-icons"
              >
                {{ $t("home.show_icons") }}
              </label>
            </div> -->
            <div class="btn-group">
              <button :class="'btn btn-primary text-white ' +
                (listOrCard === 'card' ? 'active' : '')
                " @click="listOrCard = 'card'">
                <font-awesome-icon :icon="['far', 'grid']" />
              </button>
              <button :class="'btn btn-primary text-white ' +
                (listOrCard === 'list' ? 'active' : '')
                " @click="listOrCard = 'list'">
                <font-awesome-icon :icon="['far', 'list']" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="error" class="col-12 mb-3">
        <div class="alert alert-danger" role="alert">
          {{ errorMessage }}
        </div>
      </div>
      <div v-else-if="hasNoFilteredResults" class="col-12 mb-3">
        <div class="alert alert-secondary text-center" role="status">
          {{ $t("home.no_filtered_results") }}
        </div>
      </div>

      <template v-else>
        <template v-if="listOrCard === 'card'">
          <div class="col col-sm-12 col-md-6 col-xl-3 col-12 mb-4 d-flex" v-for="item in filteredTools" :key="item.id">
            <div class="card card-hover rounded w-100">
              <div class="card-body d-flex flex-column">
                <div v-if="showIcons" class="rounded bg-primary-subtle mb-4 card-icon" style="
                    height: 64px;
                    width: 64px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  ">
                  <ImageBlob v-if="item.data.icon" class="card-icon-image" :imagePath="item.data.icon" />
                  <font-awesome-icon v-else :icon="['far', 'face-confused']" size="2x" class="text-primary" />
                </div>
                <ImageBlob v-else class="card-image mb-3" style="height: 170px; width: 100%; object-fit: contain"
                  :imagePath="item.data.image" />
                <h5 class="card-title fw-medium mb-3 text-center">{{ item.data.name }}</h5>
                <div v-if="item.data.maintenance?.enabled" class="d-flex justify-content-center mb-2">
                  <span class="maintenance-pill badge rounded-pill">
                    <font-awesome-icon :icon="['far', 'triangle-exclamation']" class="me-1" />
                    {{ $t("home.maintenance_active") }}
                  </span>
                </div>
                <!-- <p class="card-text text-secondary mb-4">
                  {{ item.data.description || $t("home.default_description") }}
                </p> -->
                <div class="d-flex flex-column gap-2 flex-grow-1 justify-content-end">
                  <button class="btn btn-primary text-white"
                    :disabled="loadingToken.includes(item.id) || isMaintenanceBlocked(item)"
                    @click="getToken(item.id, item.data.url, item.data.name)">
                    <span v-if="!loadingToken.includes(item.id)">
                      <template v-if="isMaintenanceBlocked(item)">
                        {{ $t("home.maintenance_blocked") }}
                      </template>
                      <template v-else>
                        <font-awesome-icon :icon="['far', 'arrow-up-right-from-square']" class="me-2" />
                        {{ $t("home.login") }}
                      </template>
                    </span>
                    <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  </button>
                  <button v-if="user.claims.state === 'superadmin'" class="btn btn-outline-primary hover:text-white"
                    style="--bs-btn-hover-color: white" @click="copyToken(item.id, item.data.name)"
                    :disabled="loadingOnlyToken.includes(item.id)">
                    <span v-if="!loadingOnlyToken.includes(item.id)">
                      Token
                    </span>
                    <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="col-12 mb-3" v-for="item in filteredTools" :key="item.id">
            <div class="card card-hover w-100 d-flex">
              <div class="row g-0">
                <div class="col-md-12">
                  <div class="card-body d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center gap-3">
                      <div v-if="showIcons" class="rounded bg-primary-subtle card-icon" style="
                          height: 64px;
                          width: 64px;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                        ">
                        <ImageBlob v-if="item.data.icon" class="card-icon-image" :imagePath="item.data.icon" />
                        <font-awesome-icon v-else :icon="['far', 'face-confused']" size="2x" class="text-primary" />
                      </div>
                      <div v-else class="d-flex align-items-center d-xs-none">
                        <ImageBlob style="height: 56px; min-width: 65px;" :imagePath="item.data.image" />
                      </div>
                      <div class="d-flex flex-column justify-items-center">
                        <h5 class="card-title fw-medium mb-0">{{ item.data.name }}</h5>
                        <span v-if="item.data.maintenance?.enabled"
                          class="maintenance-pill badge rounded-pill mt-1 align-self-start">
                          <font-awesome-icon :icon="['far', 'triangle-exclamation']" class="me-1" />
                          {{ $t("home.maintenance_active") }}
                        </span>
                        <!-- <p class="card-text text-secondary">
                          {{
                            item.data.description ||
                            $t("home.default_description")
                          }}
                        </p> -->
                      </div>
                    </div>
                    <div class="d-flex gap-2 flex-column flex-md-row-reverse">
                      <button class="btn btn-primary w-100 text-white"
                        :disabled="loadingToken.includes(item.id) || isMaintenanceBlocked(item)" @click="
                          getToken(item.id, item.data.url, item.data.name)
                          ">
                        <span v-if="!loadingToken.includes(item.id)" class="text-nowrap">
                          <template v-if="isMaintenanceBlocked(item)">
                            {{ $t("home.maintenance_blocked") }}
                          </template>
                          <template v-else>
                            <font-awesome-icon :icon="['far', 'arrow-up-right-from-square']" class="me-2" />
                            {{ $t("home.login") }}
                          </template>
                        </span>
                        <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      </button>
                      <button v-if="user.claims.state === 'superadmin'"
                        class="btn btn-outline-primary w-100 hover:text-white" style="--bs-btn-hover-color: white"
                        @click="copyToken(item.id, item.data.name)" :disabled="loadingOnlyToken.includes(item.id)">
                        <span v-if="!loadingOnlyToken.includes(item.id)">
                          Token
                        </span>
                        <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>

    <div class="modal fade" tabindex="-1" ref="modal" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t("home.token") }}</h5>
            <button type="button" class="btn btn-close" @click="modal.hide()" aria-label="Close" />
          </div>

          <div class="modal-body">
            <textarea class="form-control" aria-label="With textarea" v-model="generatedToken"></textarea>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "./LoadingComponent.vue";
import { httpsCallable } from "firebase/functions";
import { functions } from "../plugins/firebase";
import {
  query,
  collection,
  where,
  orderBy,
  getFirestore,
  getDocs,
} from "firebase/firestore";
import { mapGetters } from "vuex";
import { Modal } from "@comlinedev/bootstrap5";
import ImageBlob from "./smallComponents/ImageBlob.vue";
import {
  linkWithPopup,
  OAuthProvider
} from "firebase/auth";
import { auth } from "../plugins/firebase";

export default {
  name: "HomeView",
  components: {
    Loading,
    ImageBlob,
  },
  data() {
    return {
      loading: true,
      tools: [],
      error: null,
      loadingToken: [],
      loadingOnlyToken: [],
      listOrCard: localStorage.getItem("clgmbh-portal-app-display") || "card",
      generatedToken: "",
      showIcons:
        localStorage.getItem("clgmbh-portal-app-item-icons") === "true",
      searchTerm: "",
      linkingMicrosoft: false,
    };
  },
  computed: {
    ...mapGetters({
      user: "user",
      microsoftLinked: "microsoftLinked",
    }),
    filteredTools() {
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) {
        return this.tools;
      }
      return this.tools.filter((tool) => {
        const name = tool.data?.name?.toLowerCase() || "";
        const description = tool.data?.description?.toLowerCase() || "";
        return name.includes(term) || description.includes(term);
      });
    },
    hasNoTools() {
      return !this.loading && this.tools.length === 0 && !this.error;
    },
    hasNoFilteredResults() {
      return (
        !this.loading &&
        !this.error &&
        this.tools.length > 0 &&
        this.filteredTools.length === 0
      );
    },
    errorMessage() {
      if (!this.error) {
        return "";
      }
      return this.error.message || this.$t("home.error_loading");
    },
  },
  watch: {
    listOrCard(val) {
      localStorage.setItem("clgmbh-portal-app-display", val);
    },
    showIcons(val) {
      localStorage.setItem(
        "clgmbh-portal-app-item-icons",
        val ? "true" : "false"
      );
    },
  },
  methods: {
    isMaintenanceBlocked(tool) {
      return !!(
        tool?.data?.maintenance?.enabled &&
        this.user?.claims?.state !== "superadmin"
      );
    },
    getMaintenanceMessage() {
      return this.$t("home.maintenance_message_default");
    },
    async loadData() {
      this.loading = true;
      this.error = null;
      try {
        const q = query(
          collection(getFirestore(), "tools"),
          where("users", "array-contains", this.user.data.uid),
          orderBy("name")
        );

        const querySnapshot = await getDocs(q);
        this.tools = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          data: doc.data(),
        }));
      } catch (err) {
        this.error = err;
        this.$store.commit("addError", err);
      } finally {
        this.loading = false;
      }
    },
    async getToken(id, url, name) {
      const tool = this.tools.find((item) => item.id === id);
      if (this.isMaintenanceBlocked(tool)) {
        this.$store.commit("addError", this.getMaintenanceMessage(tool));
        return;
      }

      if (this.loadingToken.includes(id)) {
        return;
      }

      this.loadingToken.push(id);
      const newWindow = this.openWindow("", name);
      if (!newWindow) {
        this.loadingToken = this.loadingToken.filter((item) => item !== id);
        return;
      }

      const getUserTokenFromTool = httpsCallable(
        functions,
        "getUserTokenFromTool"
      );

      try {
        const response = await getUserTokenFromTool({ toolId: id, name });
        const token = response.data;
        window.postMessage({ authToken: token }, "*");
        newWindow.location.href = `${url}${token}`;
      } catch (err) {
        this.$store.commit("addError", err);
        if (!newWindow.closed) {
          newWindow.close();
        }
      } finally {
        this.loadingToken = this.loadingToken.filter((item) => item !== id);
      }
    },
    openWindow(url, name) {
      const newWindow = window.open(url, name);
      if (!newWindow) {
        alert("Please unblock popups in your browser settings.");
      } else if (window.focus) {
        newWindow.focus();
      }
      return newWindow;
    },
    async copyToken(id, name) {
      const tool = this.tools.find((item) => item.id === id);
      if (this.isMaintenanceBlocked(tool)) {
        this.$store.commit("addError", this.getMaintenanceMessage(tool));
        return;
      }

      if (this.loadingOnlyToken.includes(id)) {
        return;
      }

      this.loadingOnlyToken.push(id);
      const getUserTokenFromTool = httpsCallable(
        functions,
        "getUserTokenFromTool"
      );

      try {
        const response = await getUserTokenFromTool({ toolId: id, name });
        const token = response.data;
        this.generatedToken = token;

        if (
          typeof navigator !== "undefined" &&
          navigator.clipboard &&
          window.isSecureContext
        ) {
          try {
            await navigator.clipboard.writeText(token);
          } catch (clipboardError) {
            // Clipboard copy failed silently - token still shown in modal
          }
        }

        this.modal.show();
      } catch (err) {
        this.$store.commit("addError", err);
      } finally {
        this.loadingOnlyToken = this.loadingOnlyToken.filter(
          (item) => item !== id
        );
      }
    },
    async linkMicrosoftAccount() {
      if (!auth.currentUser) {
        this.$store.commit(
          "addError",
          this.$t("home.microsoft_account_auth_required")
        );
        return;
      }

      if (this.getLinkingMicrosoftState()) {
        return;
      }

      this.linkingMicrosoft = true;

      try {
        const provider = new OAuthProvider("microsoft.com");
        provider.setCustomParameters({ prompt: "select_account", tenant: process.env.VUE_APP_SETUP_TENANT_ID });
        const result = await linkWithPopup(auth.currentUser, provider);
        await this.$store.dispatch("fetchUser", result.user);
        this.$store.commit(
          "addSuccess",
          this.$t("home.microsoft_account_link_success")
        );
      } catch (error) {
        console.error("Microsoft account linking failed:", error);
        this.$store.commit(
          "addError",
          this.getMicrosoftLinkErrorMessage(error)
        );
      } finally {
        this.linkingMicrosoft = false;
      }
      return;
    },
    getMicrosoftLinkErrorMessage(error) {
      const errorMessages = {
        "auth/provider-already-linked": "home.microsoft_account_already_linked",
        "auth/credential-already-in-use": "home.microsoft_account_in_use",
        "auth/popup-blocked": "home.microsoft_popup_blocked",
        "auth/popup-closed-by-user": "home.microsoft_popup_closed",
        "auth/cancelled-popup-request": "home.microsoft_popup_cancelled",
      };
      const translationKey =
        errorMessages[error?.code] || "home.microsoft_account_link_error";

      return this.$t(translationKey);
    },
    getLinkingMicrosoftState() {
      return !!auth.currentUser?.providerData?.some(
        ({ providerId }) => providerId === "microsoft.com"
      );
    }
  },
  mounted() {
    this.modal = new Modal(this.$refs.modal);
    this.loadData();
  },
};
</script>

<style scoped>
.card-hover {
  transition: transform 0.2s;
}

.card-hover:hover {
  transform: scale(1.01) translateY(-3px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.card-hover .card-icon {
  transition: transform 0.2s;
}

.card-hover:hover .card-icon {
  transform: scale(1.05);
}

.card-icon-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 12px;
}

.maintenance-pill {
  background: #ffc107;
  color: #212529;
  font-weight: 700;
  letter-spacing: 0.01em;
  border: 1px solid #e6ac00;
  padding: 0.4rem 0.7rem;
}

.sign-in-with-microsoft {
  position: relative;
  overflow: hidden;
  animation: signInWithMicrosoft 0.5s ease-in-out 0s 1 normal forwards;
}

.sign-in-with-microsoft::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.296),
      transparent);
  transform: translateX(-120%) skewX(-20deg);
  animation: microsoftLightWave 20s ease-in-out 0.5s infinite;
}

@keyframes signInWithMicrosoft {
  0% {
    transform: scale(0.95);
    opacity: 0.7;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes microsoftLightWave {
  0% {
    transform: translateX(-120%) skewX(-20deg);
  }

  6%,
  100% {
    transform: translateX(120%) skewX(-20deg);
  }
}
</style>
